import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
  Eye,
  Calculator,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Game {
  id: number;
  name: string;
  creator: number;
  isStarted: boolean;
  isCompleted: boolean;
  isConsolidated: boolean;
  gameType: string;
  createdAt: string;
}

export function GameRoomsTab() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const {
    data: games,
    isLoading,
    refetch,
  } = useQuery<Game[]>({
    queryKey: ["admin-games"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/games");
      return response as Game[];
    },
  });

  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Mutation for consolidating game points
  const consolidatePointsMutation = useMutation({
    mutationFn: async (gameId: number) => {
      const response = await apiRequest(`/api/admin/games/${gameId}/consolidate-points`, {
        method: "POST"
      });
      return response;
    },
    onSuccess: () => {
      toast({
        title: "Points consolidated",
        description: "Game points have been successfully consolidated",
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin-games"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error consolidating points",
        description:
          error.message ?? "An error occurred while consolidating points",
        variant: "destructive",
      });
    },
  });

  const handleViewGameDetails = (gameId: number) => {
    setLocation(`/game-history/${gameId}`);
  };

  const handleConsolidatePoints = (gameId: number) => {
    consolidatePointsMutation.mutate(gameId);
  };

  const getGameStatus = (game: Game) => {
    if (game.isCompleted) return "completed";
    if (game.isStarted) return "in_progress";
    return "pending";
  };

  const getGameStatusStyle = (game: Game) => {
    if (game.isCompleted) return "bg-emerald-600 hover:bg-emerald-700 text-white";
    if (game.isStarted) return "bg-blue-600 hover:bg-blue-700 text-white";
    return "bg-gray-600 hover:bg-gray-700 text-white";
  };

  const getGameStatusText = (game: Game) => {
    if (game.isCompleted) return "Completed";
    if (game.isStarted) return "In Progress";
    return "Pending";
  };

  // Filter games based on search query and filters
  const filteredGames = games
    ? games.filter((game: Game) => {
        // Search filter
        const matchesSearch = [
          game.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          game.creator?.toString().includes(searchQuery) ?? false,
          game.id?.toString().includes(searchQuery) ?? false
        ].some(Boolean);

        // Status filter
        const status = getGameStatus(game);
        const matchesStatus = statusFilter === "all" || statusFilter === status;

        // Type filter
        const matchesType =
          typeFilter === "all" ||
          (game.gameType && game.gameType === typeFilter);

        return matchesSearch && matchesStatus && matchesType;
      })
    : [];

  // Get unique game types for the filter dropdown
  const gameTypes = games
    ? Array.from(new Set(games.map((game) => game.gameType))).filter(Boolean)
    : [];

  if (isLoading) {
    return <div className="text-center py-8">Loading game rooms...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-semibold">Game Room Management</h2>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, ID or creator..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
          </select>

          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            {gameTypes.map((type) => (
              <option key={String(type)} value={String(type)}>
                {String(type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!games || games.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No game rooms found
        </div>
      ) : filteredGames.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No game rooms match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Creator</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((game) => (
                <tr key={game.id} className="border-t hover:bg-muted/50">
                  <td className="p-2">{game.id}</td>
                  <td className="p-2">{game.name}</td>
                  <td className="p-2">{game.creator}</td>
                  <td className="p-2">
                    <Badge className={getGameStatusStyle(game)}>
                      {getGameStatusText(game)}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <Badge className="bg-muted hover:bg-muted/80 text-foreground">
                      {game.gameType}
                    </Badge>
                  </td>
                  <td className="p-2">
                    {new Date(game.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewGameDetails(game.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className={
                          !game.isCompleted || game.isConsolidated
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }
                        disabled={!game.isCompleted || game.isConsolidated}
                        onClick={() => handleConsolidatePoints(game.id)}
                      >
                        <Calculator className="h-4 w-4 mr-1" />
                        Consolidate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}